import { useRouter } from "next/router";
import { InferGetStaticPropsType } from "next";
import Link from "next/link";

import { AppShell } from "@chef/feature-app-shell";
import { StepperPagination } from "@chef/smart-components";
import { RecipeActionModal } from "@chef/smart-components";
import { BRAND_PATHS } from "@chef/constants";
import { BlockContent, useRecipesCategoryData } from "@chef/feature-sanity";
import { Button, FloatingActionBar } from "@chef/components";
import { isNotEmptyArray } from "@chef/utils/array";
import { useRecipesByIngredientCategoriesQuery } from "@chef/state-management";

import { CategoryPageGetStaticProps, intl } from "..";
import { CategoryList } from "../components/CategoryList";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { RecipeList } from "../components/RecipeList";

export const CategoryPage = ({
  rootCategories,
  ingredientCategoryIds,
  breadcrumbs,
  recipesCategory,
  page,
}: InferGetStaticPropsType<typeof CategoryPageGetStaticProps>) => {
  const { categoryPath } = useRouter().query;
  const isCategoryPage =
    Array.isArray(categoryPath) && isNotEmptyArray(categoryPath);

  const { data: recipesByIngredientCategoriesQuery } =
    useRecipesByIngredientCategoriesQuery({
      pageNumber: page,
      ingredientCategoryIds,
      resultsPerPage: 30,
    });

  const parentPath = isCategoryPage
    ? `${BRAND_PATHS.RECIPE_HUB_PATH}/${categoryPath.join("/")}`
    : BRAND_PATHS.RECIPE_HUB_PATH;

  const [recipesCategoryContent] = useRecipesCategoryData(recipesCategory);
  const { topSection, bottomSection } = recipesCategoryContent || {};

  const subcategoryHeading =
    (breadcrumbs[breadcrumbs.length - 1].value as string) || intl.RECIPES;

  const title = isCategoryPage
    ? `${subcategoryHeading} - ${intl.RECIPE}`
    : subcategoryHeading;

  if (!recipesByIngredientCategoriesQuery) {
    return null;
  }

  return (
    <AppShell title={title}>
      <AppShell.Meta />
      <AppShell.Header />
      <AppShell.Main className="text-center">
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        {isCategoryPage && (
          <h1 className="mt-10 mb-2 text-3xl text-center">
            <strong>{subcategoryHeading}</strong>
          </h1>
        )}
        {topSection && <BlockContent body={topSection} />}
        <CategoryList
          categories={
            isCategoryPage
              ? rootCategories
              : [...rootCategories, { value: intl.ALL, slug: intl.CATEGORIES }]
          }
          parentPath={parentPath}
        />
        <RecipeList
          recipes={
            recipesByIngredientCategoriesQuery.getRecipesByIngredientCategories
              .recipes
          }
        />
        <StepperPagination
          page={page}
          total={
            recipesByIngredientCategoriesQuery.getRecipesByIngredientCategories
              .totalPages
          }
        />
        {bottomSection && <BlockContent body={bottomSection} />}
        <RecipeActionModal />
      </AppShell.Main>
      <FloatingActionBar className="sticky">
        <div className="col-span-2 col-start-3 md:col-span-3 md:col-start-6 xl:col-start-10">
          <Link href={BRAND_PATHS.SIGNUP_HREF}>
            <Button primary full>
              {intl.ORDER_MEALBOX}
            </Button>
          </Link>
        </div>
      </FloatingActionBar>
      <AppShell.Footer />
    </AppShell>
  );
};
